<template>
    <v-dialog v-model="internalDialog" max-width="800" scrollable>
        <v-card>
            <v-card-title class="primary white--text">
                <span class="text-h6">Get to Know {{ user ? firstName : 'You' }}</span>
                <v-spacer></v-spacer>
                <v-btn icon dark @click="close">
                    <v-icon>mdi-close</v-icon>
                </v-btn>
            </v-card-title>
            <v-card-text class="pt-6">
                <div class="d-flex align-center mb-4" v-if="user">
                    <v-avatar color="primary" size="80" class="mr-4 flex-shrink-0">
                        <v-img v-if="currentProfilePicture" :src="getProfilePictureUrl(currentProfilePicture)"></v-img>
                        <span v-else class="white--text text-h5">{{ initials }}</span>
                    </v-avatar>
                    <v-file-input v-model="profilePictureFile" label="Profile Picture" outlined dense
                        accept="image/*" prepend-icon="" prepend-inner-icon="mdi-camera"
                        @change="uploadProfilePicture" :loading="uploadingPicture" hint="Upload an image file"
                        persistent-hint show-size></v-file-input>
                </div>
                <v-form ref="form">
                    <v-row dense>
                        <v-col cols="12" sm="6">
                            <v-menu v-model="showDatePicker" :close-on-content-click="false" offset-y>
                                <template v-slot:activator="{ on, attrs }">
                                    <v-text-field v-model="form.start_date" label="Start Date" readonly outlined
                                        dense prepend-inner-icon="mdi-calendar" v-bind="attrs" v-on="on"
                                        clearable></v-text-field>
                                </template>
                                <v-date-picker v-model="form.start_date" @input="showDatePicker = false"></v-date-picker>
                            </v-menu>
                        </v-col>
                        <v-col cols="12" sm="6">
                            <v-text-field v-model="form.from_location" label="Where are you from?" outlined dense></v-text-field>
                        </v-col>
                        <v-col cols="12" sm="6">
                            <v-autocomplete v-model="form.unit" :items="companies()" item-text="name"
                                item-value="name" label="Unit" outlined dense clearable></v-autocomplete>
                        </v-col>
                        <v-col cols="12" sm="6">
                            <v-text-field v-model="form.title" label="Title" outlined dense></v-text-field>
                        </v-col>
                        <v-col cols="12">
                            <v-text-field v-model="form.company_email" label="Company Email" type="email" outlined
                                dense></v-text-field>
                        </v-col>
                        <v-col cols="12" sm="6">
                            <v-textarea v-model="form.what_i_actually_do" label="What I Actually Do" outlined dense
                                rows="2"></v-textarea>
                        </v-col>
                        <v-col cols="12" sm="6">
                            <v-textarea v-model="form.hobbies" label="Hobbies" outlined dense rows="2"></v-textarea>
                        </v-col>
                        <v-col cols="12" sm="6">
                            <v-textarea v-model="form.ask_me_about" label="Ask Me About" outlined dense
                                rows="2"></v-textarea>
                        </v-col>
                        <v-col cols="12" sm="6">
                            <v-textarea v-model="form.fun_fact" label="Fun Fact" outlined dense rows="2"></v-textarea>
                        </v-col>
                    </v-row>
                </v-form>
            </v-card-text>
            <v-card-actions class="pa-4">
                <v-spacer></v-spacer>
                <v-btn text @click="close">Skip for now</v-btn>
                <v-btn color="primary" :loading="saving" @click="save">Save</v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
const EMPTY_FORM = {
    start_date: null,
    from_location: null,
    unit: null,
    title: null,
    company_email: null,
    what_i_actually_do: null,
    hobbies: null,
    ask_me_about: null,
    fun_fact: null,
};

export default {
    name: 'FloorPlanUserDetailsDialog',
    inject: ['companies', 'updateUser'],
    props: {
        value: { type: Boolean, default: false },
        user: { type: Object, default: null },
        details: { type: Object, default: null },
        saving: { type: Boolean, default: false },
    },
    data() {
        return {
            internalDialog: this.value,
            showDatePicker: false,
            form: { ...EMPTY_FORM },
            profilePictureFile: null,
            uploadingPicture: false,
            uploadedProfilePicture: null,
        };
    },
    computed: {
        apiUrl() {
            return process.env.VUE_APP_API_URL || '';
        },
        firstName() {
            if (!this.user) return '';
            return this.user.first_name || (this.user.name || '').split(' ')[0];
        },
        currentProfilePicture() {
            return this.uploadedProfilePicture || (this.user && this.user.profile_picture);
        },
        initials() {
            if (!this.user || !this.user.name) return '?';
            const names = this.user.name.split(' ');
            return names.length >= 2 ? (names[0][0] + names[1][0]).toUpperCase() : this.user.name.substring(0, 2).toUpperCase();
        },
    },
    watch: {
        value(val) {
            this.internalDialog = val;
            if (val) {
                this.resetForm();
            }
        },
        internalDialog(val) {
            this.$emit('input', val);
        },
        details() {
            if (this.internalDialog) {
                this.resetForm();
            }
        },
    },
    methods: {
        close() {
            this.internalDialog = false;
        },
        resetForm() {
            this.form = { ...EMPTY_FORM, ...(this.details || {}) };
            this.uploadedProfilePicture = null;
        },
        save() {
            this.$emit('save', { ...this.form });
        },
        getProfilePictureUrl(path) {
            if (!path) return '';
            if (path.startsWith('http://') || path.startsWith('https://')) return path;
            return `${this.apiUrl}/${path}`;
        },
        async uploadProfilePicture(file) {
            if (!file || !this.user) return;

            const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
            if (!allowedTypes.includes(file.type)) {
                this.$toast.error('Please select a valid image file (JPEG, PNG, GIF)');
                this.profilePictureFile = null;
                return;
            }

            const maxSize = 5 * 1024 * 1024; // 5MB
            if (file.size > maxSize) {
                this.$toast.error('File size must be less than 5MB');
                this.profilePictureFile = null;
                return;
            }

            this.uploadingPicture = true;
            try {
                const formData = new FormData();
                formData.append('profile_picture', file);
                formData.append('user_id', this.user.id);

                const response = await this.$axios.post(
                    'api/users/upload_profile_picture',
                    formData,
                    { headers: { 'Content-Type': 'multipart/form-data' } },
                );

                if (response.data.response) {
                    this.uploadedProfilePicture = response.data.data.profile_picture;
                    this.updateUser({ ...this.user, profile_picture: response.data.data.profile_picture });

                    const currentUser = this.$store.getters['auth/get_user'];
                    if (currentUser && currentUser.id === this.user.id) {
                        this.$store.commit('auth/set_user', { ...currentUser, profile_picture: response.data.data.profile_picture });
                    }

                    this.$toast.success('Profile picture uploaded successfully');
                } else {
                    this.$toast.error(response.data.message || 'Failed to upload profile picture');
                }
            } catch (error) {
                console.error('Error uploading profile picture:', error);
                this.$toast.error('Failed to upload profile picture');
            } finally {
                this.uploadingPicture = false;
                this.profilePictureFile = null;
            }
        },
    },
};
</script>
